import React, { useState } from 'react';

import { requestNewLink } from '../services/candidateLinks';
import { getTranslations } from '../services/translations';

interface Props {
  children: React.ReactNode;
  /** The candidate's language. English when not given. */
  language?: string;
}

/**
 * The frame both candidate link pages sit in.
 *
 * Deliberately unbranded beyond Orbio itself. A candidate reaches these from a
 * message rather than from a careers site, so there is no client theme loaded
 * here and nothing to paint from; the only name on the page is the one the API
 * returns.
 */
export const CandidateLinkShell: React.FC<Props> = ({ children, language = 'en' }) => (
  <div className="orbio-link-page min-h-screen px-4 py-10 sm:py-16">
    <div className="mx-auto w-full max-w-xl">
      <div
        className="rounded-2xl bg-white px-6 py-8 sm:px-10 sm:py-10"
        style={{ border: '1px solid var(--link-rule)' }}
      >
        <p
          className="mb-6 flex items-center gap-2 text-xs font-medium tracking-wide"
          style={{ color: 'var(--link-ink-soft)' }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M6 10V7a6 6 0 1 1 12 0v3m-13 0h14v11H5V10Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </svg>
          {getTranslations(language)['candidate_link.secure_upload']}
        </p>
        {children}
      </div>
      <div className="mt-6 flex justify-center">
        {/* The mark already carries the wordmark, so it is the whole footer. */}
        <img src="/orbio-logo.svg" alt="Orbio" className="h-4 w-auto opacity-50" />
      </div>
    </div>
  </div>
);

export const CandidateLinkSpinner: React.FC = () => (
  <div className="flex justify-center py-6">
    <div
      className="h-8 w-8 animate-spin rounded-full border-2 border-transparent"
      style={{ borderTopColor: 'var(--link-action)', borderRightColor: 'var(--link-action)' }}
    />
  </div>
);

/**
 * What both pages show when the code no longer works.
 *
 * Says only that the link has expired, and offers one deliberate action. Nothing
 * is minted by arriving here: the new link is sent because the candidate pressed
 * the button, and it leaves on the channel already on file, so this asks for no
 * number and shows none.
 */
export const ExpiredLink: React.FC<{ code: string; language?: string }> = ({
  code,
  language = 'en',
}) => {
  const tr = getTranslations(language);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'spent' | 'failed'>('idle');

  const ask = async () => {
    setState('sending');
    try {
      const status = await requestNewLink(code);
      setState(status === 'limit_reached' ? 'spent' : 'sent');
    } catch {
      setState('failed');
    }
  };

  return (
    <CandidateLinkShell language={language}>
      <h1 className="text-2xl">{tr['candidate_link.expired_title']}</h1>

      {state === 'sent' && (
        <p className="mt-4 text-base" style={{ color: 'var(--link-ink-soft)' }}>
          {tr['candidate_link.expired_sent']}
        </p>
      )}

      {state === 'spent' && (
        <p className="mt-4 text-base" style={{ color: 'var(--link-ink-soft)' }}>
          {tr['candidate_link.expired_limit']}
        </p>
      )}

      {state !== 'sent' && state !== 'spent' && (
        <>
          <p className="mt-4 text-base" style={{ color: 'var(--link-ink-soft)' }}>
            {tr['candidate_link.expired_offer']}
          </p>
          {state === 'failed' && (
            <p className="mt-4 text-sm" style={{ color: '#a33' }}>
              {tr['candidate_link.expired_failed']}
            </p>
          )}
          <button
            type="button"
            onClick={ask}
            disabled={state === 'sending'}
            className="orbio-link-button mt-8 w-full rounded-xl px-4 py-3.5 text-base font-medium"
          >
            {state === 'sending'
              ? tr['candidate_link.expired_sending']
              : tr['candidate_link.expired_button']}
          </button>
        </>
      )}
    </CandidateLinkShell>
  );
};
